import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { AlertTriangle } from 'lucide-react';
import { API_BASE } from '../config/api';

// Canonical error types — keys must match the backend (error_report_routes.py).
const ERROR_TYPES = [
  { key: 'wrong_answer', label: 'Đáp án sai', needsQuestions: 'wrong' },
  { key: 'mis_graded', label: 'Chấm đáp án chưa chính xác', needsQuestions: 'graded' },
  { key: 'spelling', label: 'Lỗi chính tả', descPlaceholder: 'Mô tả rõ thông tin: ghi rõ từ/cụm từ nào bị sai và nội dung đề xuất sửa.' },
  { key: 'audio', label: 'Lỗi âm thanh' },
  // Timestamps for most questions are matched automatically, so a few land on the wrong
  // moment. The question number is what makes a report actionable.
  { key: 'audio_cue', label: 'Nghe lại nhảy sai đoạn', descPlaceholder: 'Ghi rõ câu số mấy nhảy sai (vd: câu 12, 15) — nếu biết đoạn đúng nằm ở phút nào thì càng tốt.' },
  { key: 'ui', label: 'Lỗi giao diện / hiển thị', descPlaceholder: 'Mô tả rõ thông tin: vị trí và nội dung bị lỗi (vd: sai bố cục, chữ bị che, hiển thị không đúng, nút không hoạt động...).' },
  { key: 'other', label: 'Khác' },
];

/**
 * Báo lỗi (report error) modal, filed from the exam Review screen.
 * Props: open, onClose, examId, resultId, skill ('reading'|'listening'), examTitle.
 */
const ErrorReportModal = ({ open, onClose, examId, resultId, skill, examTitle }) => {
  const [selected, setSelected] = useState([]);
  const [wrongQuestions, setWrongQuestions] = useState('');
  const [misGradedQuestions, setMisGradedQuestions] = useState('');
  const [typeInfo, setTypeInfo] = useState({});   // per-type "Mô tả rõ thông tin" (spelling / ui)
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const toggle = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const reset = () => {
    setSelected([]);
    setWrongQuestions('');
    setMisGradedQuestions('');
    setTypeInfo({});
    setDescription('');
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (selected.length === 0) {
      toast.error('Vui lòng chọn ít nhất một loại lỗi.');
      return;
    }
    // Fold the per-type "Mô tả rõ thông tin" (spelling / ui) into the description so
    // the admin sees structured detail without a backend schema change.
    const descParts = [];
    ERROR_TYPES.forEach((t) => {
      if (t.descPlaceholder && selected.includes(t.key) && (typeInfo[t.key] || '').trim()) {
        descParts.push(`[${t.label}] ${typeInfo[t.key].trim()}`);
      }
    });
    if (description.trim()) descParts.push(description.trim());
    const fullDescription = descParts.join('\n') || null;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/student/error-report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exam_id: examId ? parseInt(examId) : null,
          result_id: resultId ? parseInt(resultId) : null,
          skill: skill || null,
          exam_title: examTitle || null,
          error_types: selected,
          wrong_answer_questions: selected.includes('wrong_answer') ? wrongQuestions.trim() || null : null,
          mis_graded_questions: selected.includes('mis_graded') ? misGradedQuestions.trim() || null : null,
          description: fullDescription,
        }),
      });
      if (res.ok) {
        toast.success('Đã gửi báo lỗi. Cảm ơn bạn đã đóng góp!');
        reset();
        onClose();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Gửi báo lỗi thất bại. Vui lòng thử lại.');
      }
    } catch (e) {
      toast.error('Có lỗi xảy ra. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
          <h2 className="text-xl font-bold text-[#2b5356]">Báo lỗi</h2>
          <button
            onClick={handleClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Đóng"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4">
          <p className="mb-3 text-sm text-gray-500">Chọn loại lỗi bạn gặp phải:</p>
          <div className="space-y-2">
            {ERROR_TYPES.map((t) => {
              const checked = selected.includes(t.key);
              return (
                <div key={t.key}>
                  <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5 hover:border-[#0096b1]">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(t.key)}
                      className="h-4 w-4 rounded border-gray-300 text-[#0096b1] focus:ring-[#0096b1]"
                    />
                    <span className="text-sm font-medium text-gray-800">{t.label}</span>
                  </label>
                  {checked && t.needsQuestions === 'wrong' && (
                    <input
                      type="text"
                      value={wrongQuestions}
                      onChange={(e) => setWrongQuestions(e.target.value)}
                      placeholder="Nhập số câu có đáp án sai (vd: 3, 7, 12)"
                      className="mt-2 ml-7 w-[calc(100%-1.75rem)] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0096b1] focus:outline-none focus:ring-1 focus:ring-[#0096b1]"
                    />
                  )}
                  {checked && t.needsQuestions === 'graded' && (
                    <input
                      type="text"
                      value={misGradedQuestions}
                      onChange={(e) => setMisGradedQuestions(e.target.value)}
                      placeholder="Nhập số câu bị chấm sai (vd: 5, 9)"
                      className="mt-2 ml-7 w-[calc(100%-1.75rem)] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0096b1] focus:outline-none focus:ring-1 focus:ring-[#0096b1]"
                    />
                  )}
                  {checked && t.descPlaceholder && (
                    <textarea
                      value={typeInfo[t.key] || ''}
                      onChange={(e) => setTypeInfo((p) => ({ ...p, [t.key]: e.target.value }))}
                      rows={2}
                      placeholder={t.descPlaceholder}
                      className="mt-2 ml-7 w-[calc(100%-1.75rem)] resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0096b1] focus:outline-none focus:ring-1 focus:ring-[#0096b1]"
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Mô tả thêm (không bắt buộc)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Cung cấp thêm thông tin về lỗi để chúng tôi kiểm tra..."
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0096b1] focus:outline-none focus:ring-1 focus:ring-[#0096b1]"
            />
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <span>
              <b>Lưu ý:</b> Tất cả các lỗi được báo sẽ được Admin review và kiểm tra lại. Nếu xác định đây là lỗi thực sự của hệ thống, Admin sẽ điều chỉnh và khắc phục. Nếu kết quả được xác định là đúng theo kiến thức/quy tắc của hệ thống (do người dùng hiểu chưa đúng), sẽ không có sự thay đổi.
            </span>
          </div>
        </div>

        <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-100 bg-white px-6 py-4">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="rounded-lg px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Hủy
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-lg bg-[#0096b1] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#007a90] disabled:opacity-50"
          >
            {submitting ? 'Đang gửi...' : 'Gửi báo lỗi'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorReportModal;
